import { useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { useSetState } from 'react-use'

import { RequirementValue } from '@makyoapp/makyo-sdk/api/Organizations'
import { LocalizedInfo } from '@makyoapp/makyo-sdk/models/models'
import {
  IPluginFieldOptions,
  IPluginPresenter,
  IRequirement,
  PluginFieldType,
} from '@makyoapp/makyo-sdk/models/Plugins'
import { IProjectPrivacy } from '@makyoapp/makyo-sdk/models/Projects'
import { Eye24, EyeSlash24, Plus16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, ModalGrid, Radio, SimpleInput } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'

function getMultiLanguageLabel(text: LocalizedInfo, language: string) {
  return text[language] ?? text[Object.keys(text)[0]] ?? ''
}

// todo
function getInitialEditionValue(editionType: PluginFieldType) {
  if (!editionType) return null

  switch (editionType) {
    case PluginFieldType.Password:
      return ''
    case PluginFieldType.EditableList:
      return [] as any[]
    default:
      return null
  }
}

const EditableList = ({ value, onChange }: { value: string[]; onChange(value: string[]): void }) => {
  const lastRef = useRef<HTMLInputElement>(null)
  const [focused, setFocused] = useState(false)

  const domains = Array.isArray(value) ? value : []

  useEffect(() => {
    const cb = async () => {
      // wait for state update
      await 0
      if (lastRef.current && !focused) {
        setFocused(true)
        lastRef.current?.setSelectionRange(0, lastRef.current?.value.length)
        lastRef.current?.focus()
      }
    }

    cb().then(() => {
      //
    })
  }, [lastRef, focused, value])

  return (
    <div className='ltr:pl-8 rtl:pr-8'>
      <div className='py-4 text-m-m'>
        <Trans>Domain Whitelist:</Trans>
      </div>

      {domains.length > 0 && (
        <div className='flex flex-col gap-2 pb-4'>
          {domains.map((domain, i) => {
            return (
              <div className='flex-c' key={i}>
                <SimpleInput
                  value={domain}
                  ref={lastRef}
                  onChange={(e) => {
                    const value = e.currentTarget.value
                    onChange(domains.map((d, index) => (index === i ? value : d)))
                  }}
                />
                <Button
                  variant={ButtonVariant.Ghost}
                  squared
                  noPadding
                  icon={<Trash16 />}
                  onClick={() => {
                    onChange(domains.filter((_, index) => index !== i))
                  }}
                />
              </div>
            )
          })}
        </div>
      )}

      <Button
        onClick={() => {
          onChange([...domains, 'domain.com'])
          setFocused(false)
        }}
        icon={<Plus16 />}
      >
        <Trans>Add Domain</Trans>
      </Button>
    </div>
  )
}

const RequirementSection = ({
  requirement,
  value,
  setValue,
  isInitialValue,
}: {
  requirement: IRequirement
  value: RequirementValue
  setValue(opt: RequirementValue): void
  isInitialValue: boolean
}) => {
  // Access Global State
  const { language } = useDbClientContext()

  // Local State
  const [reveal, setReveal] = useState(false)

  const label = getMultiLanguageLabel(requirement._Label, language)

  // handle render form
  const renderEditionTypeForm = (option: IPluginFieldOptions) => {
    if (!option._EditionType) return null

    switch (option._EditionType) {
      case PluginFieldType.Password:
        return (
          <div className='pt-4 ltr:pl-7 rtl:pr-7'>
            <SimpleInput
              value={value.EditionValue}
              type={reveal ? 'text' : 'password'}
              onChange={(e) => {
                setValue({ EditionValue: e.currentTarget.value })
              }}
              showClear={isInitialValue}
              onClear={() => {
                setValue({ EditionValue: '' })
              }}
              after={
                !isInitialValue && (
                  <button onClick={() => setReveal(!reveal)}>
                    {reveal ? <EyeSlash24 width={16} height={16} /> : <Eye24 width={16} height={16} />}
                  </button>
                )
              }
            />
          </div>
        )
      case PluginFieldType.EditableList:
        return (
          <EditableList
            value={value.EditionValue as unknown as string[]}
            onChange={(val) => {
              setValue({ EditionValue: val as any })
            }}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className='p-6'>
      <div className='font-bold uppercase pb-8'>{label}</div>

      {requirement._Options.map((option) => {
        return (
          <div className='pb-4' key={option._Key}>
            <Radio
              onClick={() => {
                setValue({
                  Value: option._Key,
                  EditionValue: getInitialEditionValue(option._EditionType) as any,
                })
              }}
              checked={option._Key === value.Value}
              value={option._Key}
              label={getMultiLanguageLabel(option._Name, language)}
            />

            {value.Value === option._Key && renderEditionTypeForm(option)}
          </div>
        )
      })}
    </div>
  )
}

type AuthPluginSettingsModalProps = {
  plugin: IPluginPresenter
  show: boolean
  privacySettings: IProjectPrivacy
  onSubmit(value: RequirementValue): void
  onClose(): void
  isLoading?: boolean
}

export const AuthPluginSettingsModal = ({
  show,
  plugin,
  privacySettings,
  onClose,
  isLoading,
  onSubmit,
}: AuthPluginSettingsModalProps) => {
  const currentBackendRequirements = privacySettings?._AuthPlugin._BackendRequirements

  // access plugin backend
  const activeRequirementKey = plugin._Backend[0]._Key ?? 'login-method'
  const pluginBackendRequirements = plugin._Backend

  // get initial value from project privacy
  const initialValue = currentBackendRequirements ? currentBackendRequirements[activeRequirementKey] : undefined

  const parsedInitialValue = useMemo(() => {
    return initialValue ? JSON.parse(initialValue) : { Value: 'valid-email', EditionValue: null }
  }, [initialValue])

  // Local State
  const [value, setValue] = useSetState<RequirementValue>(parsedInitialValue)

  const isInitialValue = useMemo(() => {
    return JSON.stringify(value) === JSON.stringify(parsedInitialValue)
  }, [value, parsedInitialValue])

  const handleSave = () => {
    const payload = {
      [activeRequirementKey]: JSON.stringify(value),
    } as RequirementValue

    onSubmit(payload)
  }

  return (
    <ModalGrid
      show={show}
      onCancel={onClose}
      id={`auth-plugin-modal`}
      title={`${plugin._Name} Settings`}
      isLoading={isLoading}
      onConfirm={handleSave}
      onHide={onClose}
    >
      <div className='max-h-[300px] overflow-y-auto'>
        {pluginBackendRequirements.map((requirement) => {
          return (
            <RequirementSection
              key={requirement._Key}
              requirement={requirement}
              value={value}
              setValue={setValue}
              isInitialValue={isInitialValue}
            />
          )
        })}
      </div>
    </ModalGrid>
  )
}
